import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Zap, Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_video-forge-417/artifacts/8amlg66h_Logo%20futuriste%20de%20ViralSynth.com.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Lien invalide. Aucun token de reinitialisation trouve.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Mot de passe reinitialise !');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la reinitialisation');
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-12">
          <img src={LOGO_URL} alt="ViralSynth" className="w-64 h-auto" />
        </Link>

        <div className="card-surface rounded-lg p-8 md:p-10">
          {success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-12 h-12 text-[#34C759] mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-medium text-white mb-2">Mot de passe reinitialise !</h1>
              <p className="text-sm text-white/40 mb-6">
                Votre mot de passe a ete change avec succes. Vous pouvez maintenant vous connecter.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="btn-accent text-sm px-8 py-3"
                data-testid="go-to-login-btn"
              >
                Se connecter
              </button>
            </div>
          ) : error && !token ? (
            <div className="text-center" data-testid="reset-error">
              <AlertCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-medium text-white mb-2">Lien invalide</h1>
              <p className="text-sm text-white/40 mb-6">{error}</p>
              <Link to="/forgot-password" className="btn-accent text-sm px-8 py-3 inline-block">
                Demander un nouveau lien
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-medium text-white mb-2 text-center">
                Nouveau mot de passe
              </h1>
              <p className="text-sm text-white/40 text-center mb-8">
                Choisissez un nouveau mot de passe securise.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-white/50 mb-2">Nouveau mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 input-dark rounded-md text-sm"
                      placeholder="Min. 6 caracteres"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-2">Confirmer le mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 input-dark rounded-md text-sm"
                      placeholder="Retapez le mot de passe"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-accent py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reinitialiser mon mot de passe'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
